import { Grid, GridRow, Input, ModalBottomButtons } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { Form, Formik, FormikHelpers } from 'formik';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { showUserDbErrorToast } from 'utils/toasts';
import { showSuccessToast } from '../../../utils/showSuccessToast';
import { schema } from './CreateUserForm.schema';

const UpdateProfileSettingsMutationDocument = graphql(`
  mutation BpUpdateProfileSettings($where: ProfileWhere!, $update: UpdateProfileSettings!) {
    updateProfileSettings(where: $where, update: $update) {
      profiles {
        uuid
        __typename
        email
      }
    }
  }
`);

const CreateUserFormQuery = graphql(`
  query CreateUserFormData($profile: ID!, $organizationUuid: ID!) {
    profiles(where: { uuid: $profile, organization: { uuid: $organizationUuid } }) {
      __typename
      uuid
      email
    }
  }
`);

type CreateUserFormType = {
  uuid: string;
  email: string;
};

type CreateUserFormProps = {
  profileUuid: string;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const CreateUserForm = ({ profileUuid, onLoad, onClose }: CreateUserFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [, updateUser] = useMutation(UpdateProfileSettingsMutationDocument);

  const [{ data }] = useQuery({
    query: CreateUserFormQuery,
    variables: {
      profile: profileUuid,
      organizationUuid,
    },
  });

  const profile = data?.profiles[0] ?? null;

  const formikValues: CreateUserFormType = {
    uuid: profileUuid,
    email: profile?.email ?? '',
  };

  const handleSubmit = async (values: CreateUserFormType, helpers: FormikHelpers<CreateUserFormType>) => {
    onLoad(true);

    const { error } = await updateUser({
      where: { uuid: values.uuid },
      update: {
        email: values.email,
        inviteUser: true,
        frontendUri: import.meta.env.VITE_APP_REDIRECT_URI,
      },
    });

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessToast(t('profiles.invite.invitationSent'));
    }

    onLoad(false);
    helpers.resetForm();
    onClose();
  };

  return (
    <Formik<CreateUserFormType> onSubmit={handleSubmit} initialValues={formikValues} validationSchema={schema}>
      {({ errors, handleChange, values, dirty }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow spacingBottom='s' spacingTop='s'>
                {t('profiles.addEmailInfo')}
              </GridRow>
              <GridRow spacingTop='s' spacingBottom='s'>
                <Input
                  onChange={handleChange}
                  label={t('common.email')}
                  name='email'
                  value={values.email}
                  type='email'
                  error={errors.email}
                />
              </GridRow>
            </Grid>

            <ModalBottomButtons
              submitButton={{ disabled: !values.email && !dirty }}
              closeButton={{ callback: onClose }}
              errors={errors}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
